import { useEffect, useState } from "react";
import axios from "axios";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export const BOOKING_URL = "https://dingg.app/booking/salon-orgaenics-kharadi";

function Banner() {
  const [b, setB] = useState(null);
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/content/banner`)
      .then((r) => setB(r.data))
      .catch(() => {});
  }, []);
  if (!b?.enabled || !b.text) return null;
  return (
    <div className="bg-[#11261A] text-[#F9FAF8] flex items-center justify-center gap-4 px-6 py-2.5" data-testid="announcement-banner">
      <p className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-center">{b.text}</p>
      {b.link && (
        <a
          href={b.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="announcement-banner-cta"
          className="shrink-0 text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#8EA696] border-b border-[#8EA696]/50 pb-0.5 hover:text-white hover:border-white transition-colors"
        >
          {b.link_label || "Learn more"}
        </a>
      )}
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <Banner />
      <div className="backdrop-blur-xl bg-white/70 border-b border-[#E5E7E3]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group" onClick={() => setOpen(false)}>
            <img
              src="/logo-emblem.png"
              alt="Salon Orgænics emblem"
              className="w-12 h-12 md:w-14 md:h-14 object-contain transition-transform duration-500 group-hover:rotate-6"
            />
            <span className="font-display text-xl md:text-2xl tracking-[0.18em] text-[#11261A] font-medium">
              SALON ORGÆNICS
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[13px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-[#11261A] font-semibold" : "text-[#4A4A4A] hover:text-[#11261A]"
                  } after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-[#11261A] after:transition-all after:duration-300 ${
                    isActive || pathname === l.to ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now-button"
              className="ml-2 rounded-full bg-[#11261A] text-[#F9FAF8] text-[12px] uppercase tracking-[0.2em] px-6 py-2.5 transition-all duration-300 hover:bg-black hover:shadow-lg hover:-translate-y-0.5"
            >
              Book Now
            </a>
          </nav>

          <button
            className="md:hidden text-[#11261A] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="md:hidden backdrop-blur-xl bg-white/95 border-b border-[#E5E7E3]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl text-[#11261A]"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-book-now-button"
                className="rounded-full bg-[#11261A] text-[#F9FAF8] text-[12px] uppercase tracking-[0.2em] px-6 py-3 text-center"
              >
                Book Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
